/**
 * usePushNotifications.ts
 * Hook React para gerenciar notificações push do usuário.
 *
 * Uso:
 *   const { isSubscribed, isSupported, permission, subscribe, unsubscribe, loading, error } = usePushNotifications()
 */

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/shared/contexts/AuthContext'
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  getCurrentSubscription,
} from '@/lib/pushNotifications'

interface UsePushNotificationsReturn {
  /** Se o navegador suporta Web Push */
  isSupported: boolean
  /** Se o usuário já tem uma subscription ativa neste dispositivo */
  isSubscribed: boolean
  /** Status de permissão: 'default' | 'granted' | 'denied' */
  permission: NotificationPermission
  /** Ativa notificações push para o usuário */
  subscribe: () => Promise<void>
  /** Desativa notificações push para o usuário */
  unsubscribe: () => Promise<void>
  loading: boolean
  error: string | null
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const { user } = useAuth()
  const [isSupported] = useState<boolean>(isPushSupported())
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false)
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? getNotificationPermission() : 'default'
  )
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Verifica subscription existente no mount
  useEffect(() => {
    if (!isSupported) return
    getCurrentSubscription().then((sub) => {
      setIsSubscribed(!!sub)
      setPermission(getNotificationPermission())
    })
  }, [isSupported])

  const subscribe = useCallback(async () => {
    if (!user?.id) {
      setError('Usuário não autenticado.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      await subscribeToPush(user.id)
      setIsSubscribed(true)
      setPermission(getNotificationPermission())
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  const unsubscribe = useCallback(async () => {
    if (!user?.id) {
      setError('Usuário não autenticado.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      await unsubscribeFromPush(user.id)
      setIsSubscribed(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  return { isSupported, isSubscribed, permission, subscribe, unsubscribe, loading, error }
}
